import React, {useCallback, useEffect, useState} from "react";
import {message, Spin, Tag, Typography} from "antd";
import {ArrowLeft, Building2, KeyRound, Pencil} from "lucide-react";
import {Navigate, useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {APIv1} from "../../../api";
import {extractDateBySecond, getStoredUser} from "../../../utils";
import DeleteModel from "../Models/DeleteModel";
import "../../Devices/DeviceDetail/DeviceDetail.scss";
import "./MarkerMobileDetail.scss";

const {Text} = Typography;

const renderValue = (value) => value ?? "-";

const renderStatus = (isActive, t) => (
    <Tag color={isActive === true ? "green" : isActive === false ? "red" : "default"}>
        {isActive === true ? t("common.active") : isActive === false ? t("common.inactive") : "-"}
    </Tag>
);

const normalizeLicenseDetail = (license) => ({
    id: license.id,
    license_device_id: renderValue(license.license_device_id),
    is_active: license.is_active,
    company_inn: renderValue(license.company_inn),
    company_name: renderValue(license.company_name),
    device_serial_number: renderValue(license.device_serial_number),
    created_date: extractDateBySecond(license.created_date),
    updated_date: extractDateBySecond(license.updated_date),
});

const MarkerMobileDetail = () => {
    const {id} = useParams();
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [userData] = useState(() => getStoredUser() || {});
    const [license, setLicense] = useState(null);
    const [loading, setLoading] = useState(true);
    const isStaff = Boolean(userData?.data?.is_admin);

    const fetchLicense = useCallback(async () => {
        if (!userData.token || !id || !isStaff) {
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            const {data} = await APIv1.get(`/device/license/detail/${id}/`, {
                headers: {
                    Authorization: `Token ${userData.token}`,
                },
            });

            setLicense(normalizeLicenseDetail(data));
        } catch (error) {
            console.error("Error fetching marker mobile license detail:", error);
            message.error(t("pages.markerMobile.detail.messages.fetchError"));
            setLicense(null);
        } finally {
            setLoading(false);
        }
    }, [id, isStaff, t, userData.token]);

    useEffect(() => {
        fetchLicense();
    }, [fetchLicense]);

    if (userData.token && !isStaff) {
        return <Navigate to="/analysis" replace/>;
    }

    if (loading) {
        return (
            <div className="detail-view">
                <div className="detail-view__loading">
                    <Spin size="large"/>
                </div>
            </div>
        );
    }

    if (!license) {
        return (
            <div className="detail-view">
                <div className="detail-view__header">
                    <div className="detail-view__title">
                        <span className="detail-view__eyebrow">{t("pages.markerMobile.detail.eyebrow")}</span>
                        <h1>{t("pages.markerMobile.detail.notFound")}</h1>
                    </div>
                    <button
                        className="detail-view__action-button detail-view__action-button--secondary"
                        onClick={() => navigate(-1)}
                    >
                        <ArrowLeft size={16}/>
                        {t("common.backBtn")}
                    </button>
                </div>
            </div>
        );
    }

    const renderDetailItem = ({label, value, children}) => (
        <li className="detail-view__item" key={label}>
            <span className="detail-view__label">{label}:</span>
            <span className="detail-view__value">{children || renderValue(value)}</span>
        </li>
    );

    const renderSection = ({index, title, icon, items}) => (
        <section className="detail-view__section">
            <div className="detail-view__section-header">
                <span className="detail-view__section-index">{index}</span>
                <div className="detail-view__section-heading">
                    <span className="detail-view__section-icon">{icon}</span>
                    <h2 className="detail-view__section-title">{title}</h2>
                </div>
            </div>
            <ul className="detail-view__list">
                {items.map(renderDetailItem)}
            </ul>
        </section>
    );

    return (
        <div className="detail-view">
            <div className="detail-view__container">
                <div className="detail-view__header">
                    <div className="detail-view__title">
                        <span className="detail-view__eyebrow">{t("pages.markerMobile.detail.title")}</span>
                        <h1 className="detail-view__main-title">{license.license_device_id}</h1>
                        <span className="detail-view__subtitle">
                            <span className="detail-view__subtitle-label">{t("pages.markerMobile.listColumns.company")}:</span>
                            <Text copyable>{license.company_name}</Text>
                        </span>
                    </div>
                    <div className="detail-view__action-buttons">
                        <button
                            className="detail-view__action-button detail-view__action-button--secondary"
                            onClick={() => navigate(`/devices/marker-mobile/update/${id}`)}
                        >
                            <Pencil size={16}/>
                            {t("common.edit")}
                        </button>
                        <DeleteModel
                            id={id}
                            token={userData.token}
                            licenseDeviceId={license.license_device_id}
                        />
                        <button
                            className="detail-view__action-button detail-view__action-button--secondary"
                            onClick={() => navigate(-1)}
                        >
                            <ArrowLeft size={16}/>
                            {t("common.backBtn")}
                        </button>
                    </div>
                </div>

                <div className="detail-view__content marker-mobile-detail__content">
                    <div className="detail-view__main-info marker-mobile-detail__sections">
                        {renderSection({
                            index: "01",
                            title: t("pages.markerMobile.detail.sections.licenseInformation"),
                            icon: <KeyRound size={16}/>,
                            items: [
                                {
                                    label: t("common.id"),
                                    value: license.id,
                                },
                                {
                                    label: t("common.status"),
                                    children: renderStatus(license.is_active, t),
                                },
                                {
                                    label: t("pages.markerMobile.listColumns.licenseDeviceId"),
                                    children: <Text copyable>{license.license_device_id}</Text>,
                                },
                                {
                                    label: t("common.deviceSerialNumber"),
                                    value: license.device_serial_number,
                                },
                            ],
                        })}

                        {renderSection({
                            index: "02",
                            title: t("pages.markerMobile.detail.sections.companyInformation"),
                            icon: <Building2 size={16}/>,
                            items: [
                                {
                                    label: t("pages.markerMobile.listColumns.companyInn"),
                                    value: license.company_inn,
                                },
                                {
                                    label: t("pages.markerMobile.listColumns.company"),
                                    value: license.company_name,
                                },
                                {
                                    label: t("pages.markerMobile.listColumns.createdDate"),
                                    value: license.created_date,
                                },
                                {
                                    label: t("pages.markerMobile.listColumns.updatedDate"),
                                    value: license.updated_date,
                                },
                            ],
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MarkerMobileDetail;
